import { useState } from "react";
import { ChevronRight, ChevronDown, ArrowUpRight, ArrowUp } from "lucide-react";
import type { IssueMap, ReportCategory } from "../types";
import type { BiteLink } from "../BitePanel";
import { SECTION_TITLE, slugify } from "./config";

function prettify(name: string): string {
  return name.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}
import { SeverityBadge } from "./SeverityBadge";

// ── Collapsible toggle ───────────────────────────────────────────────────────

function Disclosure({
  label,
  defaultOpen = false,
  labelClass = "text-xs text-muted-foreground",
  children,
}: {
  label: string;
  defaultOpen?: boolean;
  labelClass?: string;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div>
      <button
        type="button"
        className={`inline-flex items-center gap-1 hover:text-foreground transition-colors ${labelClass}`}
        onClick={() => setOpen((o) => !o)}
      >
        {open ? <ChevronDown className="size-3" /> : <ChevronRight className="size-3" />}
        {label}
      </button>
      {open && <div className="mt-1">{children}</div>}
    </div>
  );
}

// ── Issues table ─────────────────────────────────────────────────────────────

type IssueRow = { id: number; entry: NonNullable<IssueMap[number]> };

const DEFAULT_VISIBLE = 15;

function IssuesTable({
  rows,
  isLesson,
  onOpenBite,
}: {
  rows: IssueRow[];
  isLesson: boolean;
  onOpenBite: (link: BiteLink) => void;
}) {
  const criticalCount = rows.filter((r) => r.entry.severity === "critical").length;
  const initialVisible = Math.max(DEFAULT_VISIBLE, criticalCount);
  const [showAll, setShowAll] = useState(false);

  if (rows.length === 0) return null;

  const visible = showAll ? rows : rows.slice(0, initialVisible);
  const hidden = rows.length - initialVisible;

  return (
    <div className="rounded-lg border overflow-hidden mt-2">
      <table className="w-full text-xs">
        <thead>
          <tr className="bg-muted/30">
            <th className="px-3 py-2 text-left font-semibold uppercase tracking-wide text-muted-foreground text-[11px] w-24">Severity</th>
            <th className="px-3 py-2 text-left font-semibold uppercase tracking-wide text-muted-foreground text-[11px]">Student</th>
            {!isLesson && (
              <th className="px-3 py-2 text-left font-semibold uppercase tracking-wide text-muted-foreground text-[11px]">Lesson</th>
            )}
            {!isLesson && (
              <th className="px-3 py-2 text-right font-semibold uppercase tracking-wide text-muted-foreground text-[11px] w-16">Slide</th>
            )}
            <th className="px-3 py-2 text-right font-semibold uppercase tracking-wide text-muted-foreground text-[11px] w-28">Issue ID</th>
          </tr>
        </thead>
        <tbody>
          {visible.map(({ id, entry }) => (
            <tr key={id} className="border-t hover:bg-muted/20 transition-colors">
              <td className="px-3 py-2"><SeverityBadge severity={entry.severity} /></td>
              <td className="px-3 py-2 text-foreground">{entry.name ?? entry.username ?? entry.userId}</td>
              {!isLesson && (
                <td className="px-3 py-2 text-muted-foreground">
                  {entry.lessonName ? `${entry.lessonName} (${entry.lessonId})` : `Lesson ${entry.lessonId}`}
                </td>
              )}
              {!isLesson && (
                <td className="px-3 py-2 text-right tabular-nums text-foreground">{entry.slideIndex}</td>
              )}
              <td className="px-3 py-2 text-right tabular-nums">
                <button
                  type="button"
                  title="View bite detail"
                  className="inline-flex items-center gap-0.5 text-blue-500 hover:text-blue-400 transition-colors group"
                  onClick={() => onOpenBite({ sessionId: entry.sessionId, sk: entry.sk, issueId: String(id) })}
                >
                  {id}
                  <ArrowUpRight className="size-3 opacity-50 group-hover:opacity-100 transition-opacity" />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {!showAll && hidden > 0 && (
        <button
          type="button"
          className="w-full py-2 text-xs text-muted-foreground hover:text-foreground hover:bg-muted/20 transition-colors border-t"
          onClick={() => setShowAll(true)}
        >
          Show {hidden} more
        </button>
      )}
      {showAll && rows.length > initialVisible && (
        <button
          type="button"
          className="w-full py-2 text-xs text-muted-foreground hover:text-foreground hover:bg-muted/20 transition-colors border-t"
          onClick={() => setShowAll(false)}
        >
          Show less
        </button>
      )}
    </div>
  );
}

// ── Slide group (lesson only) ─────────────────────────────────────────────────

function SlideGroup({
  slideIndex,
  rows,
  isLesson,
  onOpenBite,
}: {
  slideIndex: number;
  rows: IssueRow[];
  isLesson: boolean;
  onOpenBite: (link: BiteLink) => void;
}) {
  const critMaj = rows.filter(
    (r) => r.entry.severity === "critical" || r.entry.severity === "major",
  ).length;
  return (
    <Disclosure
      label={`Slide ${slideIndex} — ${rows.length} issues (${critMaj} crit/maj)`}
      labelClass="text-sm text-foreground font-medium"
    >
      <IssuesTable rows={rows} isLesson={isLesson} onOpenBite={onOpenBite} />
    </Disclosure>
  );
}

// ── Subcategory ───────────────────────────────────────────────────────────────

function SubcategorySection({
  name,
  description,
  issueIds,
  issueMap,
  isLesson,
  onOpenBite,
  anchorId,
}: {
  name: string;
  description: string;
  issueIds: number[];
  issueMap: IssueMap;
  isLesson: boolean;
  onOpenBite: (link: BiteLink) => void;
  anchorId: string;
}) {
  const rows: IssueRow[] = issueIds
    .map((id) => ({ id, entry: issueMap[id] }))
    .filter((r): r is IssueRow => r.entry != null)
    .sort((a, b) => {
      const order = ["critical", "major", "minor", "observation"];
      return order.indexOf(a.entry.severity ?? "") - order.indexOf(b.entry.severity ?? "");
    });

  const critMaj = rows.filter(
    (r) => r.entry.severity === "critical" || r.entry.severity === "major",
  ).length;

  const summaryAnchor = `summary__${anchorId.split("__")[0]}`;

  return (
    <div className="space-y-2">
      <p id={anchorId} className="text-sm font-semibold text-foreground scroll-mt-6 flex items-center gap-2">
        <span>{name} <span className="font-normal text-muted-foreground">({critMaj} crit/maj / {rows.length} total)</span></span>
        <a
          href={`#${summaryAnchor}`}
          title="Back to summary"
          className="text-muted-foreground hover:text-foreground transition-colors"
        >
          <ArrowUp className="size-3" />
        </a>
      </p>

      {description && (
        <Disclosure label="Description">
          <p className="text-sm text-foreground mt-1 leading-relaxed">{description}</p>
        </Disclosure>
      )}

      {isLesson ? (
        // Group by slide index
        (() => {
          const bySlide = new Map<number, IssueRow[]>();
          for (const row of rows) {
            const s = row.entry.slideIndex;
            if (!bySlide.has(s)) bySlide.set(s, []);
            bySlide.get(s)!.push(row);
          }
          const slides = [...bySlide.entries()].sort((a, b) => a[0] - b[0]);
          return (
            <div className="space-y-1 pl-2 border-l ml-1">
              {slides.map(([slideIndex, slideRows]) => (
                <SlideGroup
                  key={slideIndex}
                  slideIndex={slideIndex}
                  rows={slideRows}
                  isLesson={isLesson}
                  onOpenBite={onOpenBite}
                />
              ))}
            </div>
          );
        })()
      ) : (
        <IssuesTable rows={rows} isLesson={isLesson} onOpenBite={onOpenBite} />
      )}

      {rows.length === 0 && (
        <p className="text-xs text-muted-foreground italic">No issues with bite references.</p>
      )}
    </div>
  );
}

// ── Category section ──────────────────────────────────────────────────────────

function CategorySection({
  category,
  catStats,
  issueMap,
  isLesson,
  onOpenBite,
}: {
  category: ReportCategory;
  catStats: { critMaj: number; total: number };
  issueMap: IssueMap;
  isLesson: boolean;
  onOpenBite: (link: BiteLink) => void;
}) {
  return (
    <div className="space-y-4">
      <div>
        <p className="text-base font-bold text-foreground">
          {prettify(category.name)}{" "}
          <span className="font-normal text-muted-foreground text-sm">({catStats.critMaj} crit/maj / {catStats.total} total)</span>
        </p>
        <div className="border-b mt-1" />
      </div>

      {category.description && (
        <Disclosure label="Description">
          <p className="text-sm text-foreground mt-1 leading-relaxed">{category.description}</p>
        </Disclosure>
      )}

      <div className="space-y-6">
        {[...category.subcategories]
          .sort((a, b) => {
            const critMaj = (ids: number[]) =>
              ids.filter((id) => {
                const sev = issueMap[id]?.severity;
                return sev === "critical" || sev === "major";
              }).length;
            return critMaj(b.issue_ids) - critMaj(a.issue_ids);
          })
          .map((sub, i) => (
            <SubcategorySection
              key={i}
              name={sub.name}
              description={sub.description}
              issueIds={sub.issue_ids}
              issueMap={issueMap}
              isLesson={isLesson}
              onOpenBite={onOpenBite}
              anchorId={`${slugify(category.name)}__${slugify(sub.name)}`}
            />
          ))}
      </div>
    </div>
  );
}

// ── Root export ───────────────────────────────────────────────────────────────

export function FindingsByCategory({
  categories,
  issueMap,
  catStatsMap,
  isLesson,
  onOpenBite,
}: {
  categories: ReportCategory[];
  issueMap: IssueMap;
  catStatsMap: Map<string, { critMaj: number; total: number }>;
  isLesson: boolean;
  onOpenBite: (link: BiteLink) => void;
}) {
  const sorted = [...categories].sort(
    (a, b) => (catStatsMap.get(b.name)?.critMaj ?? 0) - (catStatsMap.get(a.name)?.critMaj ?? 0),
  );

  return (
    <div>
      <h2 className={`${SECTION_TITLE} mb-4`}>Findings by Category</h2>
      <div className="space-y-8">
        {sorted.map((cat, i) => (
          <CategorySection
            key={i}
            category={cat}
            catStats={catStatsMap.get(cat.name) ?? { critMaj: 0, total: 0 }}
            issueMap={issueMap}
            isLesson={isLesson}
            onOpenBite={onOpenBite}
          />
        ))}
      </div>
    </div>
  );
}
